import React from 'react';
import { Box, Grid, Typography, Button, useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { useTranslation } from 'services/localization/useTranslation';
import { RiCloseLine, RiCheckLine } from 'react-icons/ri';
import classNames from 'classnames';
import { OverbookingBannerType } from './types';
import { OrderProduct } from 'common/types';

interface Props {
	onCancel: () => void;
	onSave: () => void;
	disabled: boolean;
	isEndTime: boolean;
	products: OrderProduct[];
	overbookedBanner?: OverbookingBannerType;
}
const ConfirmationBanner = (props: Props) => {
	const { onCancel, onSave, disabled, isEndTime } = props;
	const classes = useStyles();
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const label = isEndTime
		? t('rentalView.editEndTime', 'Edit end time')
		: t('rentalView.editStartTime', 'Edit start time');

	return (
		<Box className={classes.bannerContainer}>
			<Grid container justifyContent="space-between" className={classes.banner}>
				<Grid item>
					<Typography variant="h6">{label}</Typography>
				</Grid>
				{isMobile ? (
					<Grid item>
						<Button
							onClick={onCancel}
							className={classNames(classes.mobileButton, classes.cancelMobile)}
						>
							<RiCloseLine size={24} />
						</Button>
						<Button
							disabled={disabled}
							variant="contained"
							onClick={() => onSave()}
							className={classes.mobileButton}
						>
							<RiCheckLine size={24} />
						</Button>
					</Grid>
				) : (
					<Grid item>
						<Button onClick={onCancel} className={classes.buttonMargin}>
							{t('common:actions.cancel')}
						</Button>
						<Button
							disabled={disabled}
							variant="contained"
							color="primary"
							onClick={() => onSave()}
						>
							{t('common:actions.save', 'Save')}
						</Button>
					</Grid>
				)}
			</Grid>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		bannerContainer: {
			zIndex: 1100,
			padding: theme.spacing(2, 4),
			[theme.breakpoints.down('lg')]: {
				padding: theme.spacing(2),
				position: 'sticky',
				top: 0,
				backgroundColor: theme.palette.background.paper,
			},
		},
		banner: {
			alignItems: 'center',
		},
		buttonMargin: {
			marginRight: theme.spacing(2),
		},
		mobileButton: {
			borderRadius: theme.spacing(5),
			minWidth: 0,
			textTransform: 'none',
			padding: theme.spacing(1),
			margin: theme.spacing(0, 0.5),
			backgroundColor: theme.palette.primary.main,
			color: theme.palette.primary.contrastText,
			'&:hover': {
				backgroundColor: theme.palette.primary.dark,
			},
		},
		cancelMobile: {
			backgroundColor: theme.palette.colors.tundra.main,
			'&:hover': {
				backgroundColor: theme.palette.colors.tundra.dark,
			},
		},
	}),
);

export default ConfirmationBanner;
